<!-- Bar — the calculator, both ways round.
     Type a weight to see plates per side, or tap the plates already on a bar to
     get its total.

     A TAB ROOT, so it takes the bar plus a large title and no back button.
     See src/pages/SHELL.md. Load | Count sits beside the large title on iOS; on
     Material there is no large title, so it rides in the one bar instead.

     IT WRITES NOTHING. Count mode is transient by design: what is on the bar
     right now is a question, not a record, and a screen that persisted it would
     hand you yesterday's bar tomorrow. `.appfactory/product-plan.json` says the
     same — `reads: [kit, plate]`, writes nothing.

     THE MATHS IS NOT HERE. `src/lib/loading.js` owns it — `loadBar`, `countBar`,
     `base` — because the ramp needs the identical answer and two
     implementations of plate maths is two answers to "what goes on the bar".

     NEVER ROUND SILENTLY. A calculator that quietly hands back 142.5 when you
     asked for 142.75 has told you a number you will chalk up and miss. Every
     failure says what it could not do and offers the two weights it CAN. -->
<template>
  <IonPage class="work-up bar-page">
    <AppHeader title="Bar">
      <IonButtons v-if="!isIos && hasKit" slot="end">
        <div class="wu-seg modes" role="group" aria-label="Mode">
          <button
            v-for="m in MODES"
            :key="m.id"
            type="button"
            :aria-pressed="mode === m.id"
            @click="mode = m.id"
          >{{ m.label }}</button>
        </div>
      </IonButtons>
    </AppHeader>

    <IonContent :fullscreen="true">
      <div class="screen" :style="{ '--kb': `${kb}px` }">
        <div class="head">
          <AppLargeTitle title="Bar" />
          <div v-if="isIos && hasKit" class="wu-seg modes head-seg" role="group" aria-label="Mode">
            <button
              v-for="m in MODES"
              :key="m.id"
              type="button"
              :aria-pressed="mode === m.id"
              @click="mode = m.id"
            >{{ m.label }}</button>
          </div>
        </div>

        <!-- No kit, or a kit with nothing on the rack. The bar maths needs a
             bar weight and at least one plate, so this is the whole screen. -->
        <div v-if="!hasKit" class="page empty">
          <p class="empty-line serif">No plates in your kit.</p>
          <button type="button" class="wu-btn" @click="goKit">Set up your kit</button>
        </div>

        <!-- LOAD --------------------------------------------------------- -->
        <form v-else-if="mode === 'load'" class="page load" @submit.prevent="run">
          <label class="wu-label ask-label" for="target">Target weight</label>
          <!-- The whole field is the target; the input inside it is only as
               wide as what is typed, so the unit sits against the number. -->
          <div class="wu-field ask" @click="focusField">
            <span class="sizer" :data-value="target || '0'">
              <input
                id="target"
                ref="field"
                v-model="target"
                class="entry"
                type="text"
                inputmode="decimal"
                autocomplete="off"
                enterkeyhint="go"
                placeholder="0"
                @focus="setFocused(true)"
                @blur="setFocused(false)"
              >
            </span>
            <span class="field-unit">{{ unit }}</span>
            <button type="submit" class="wu-btn go" :disabled="asked === null">Load</button>
          </div>

          <!-- WHY IT COULD NOT, DIRECTLY UNDER THE QUESTION. It sat below the
               sleeve first, and on a 390×844 phone that put the whole answer —
               the sentence and both nearest weights — off the bottom of the
               screen: you tapped Load and nothing appeared to happen.
               Words and shape, never a red border: in this app a saturated
               colour already means a weight. -->
          <div v-if="problem" class="problem" role="status">
            <p class="problem-line">{{ problem.line }}</p>
            <p v-if="problem.hint" class="problem-hint">{{ problem.hint }}</p>
            <div v-if="problem.options.length" class="options">
              <button
                v-for="o in problem.options"
                :key="o.label"
                type="button"
                class="wu-btn-quiet option"
                @click="use(o.weight)"
              >
                <span class="option-in">
                  <span class="option-weight serif">{{ o.label }}</span>
                  <span class="option-unit">{{ unit }}</span>
                </span>
                <span class="option-note">{{ o.note }}</span>
              </button>
            </div>
          </div>

          <!-- THE ANSWER: the bar, then the plates as one line. The weight was
               typed once and is not printed a second time. The stage takes
               whatever height is left and the drawing scales to it, so the
               answer stays on screen above an open keyboard. -->
          <div class="stage">
            <BarSleeve
              :stack="stack"
              :plates="kitPlates"
              :unit="unit"
              :collar-weight="collarWeight"
              :animate="loadingMotion"
              @full="full = $event"
            />
            <div class="answer" role="status">
              <template v-if="result?.ok">
                <p class="per-side">{{ perSide }}</p>
                <p v-if="result.stack.length" class="per-side-note serif">each side, inside out</p>
              </template>
              <!-- Before an answer, what the bar weighs empty — quietly, never
                   a giant number that reads as a result. -->
              <p v-else class="base-line">Empty bar {{ fmt(emptyBar) }} {{ unit }}</p>
              <p v-if="full && stack.length" class="full-line">Sleeve's full, check this fits your bar.</p>
            </div>
          </div>
        </form>

        <!-- COUNT -------------------------------------------------------- -->
        <div v-else class="page count">
          <div class="tally" role="status">
            <p class="wu-label count-label">On the bar</p>
            <p class="tally-line">
              <span class="total serif">{{ fmt(countTotal) }}</span><span class="total-unit">{{ unit }}</span>
            </p>
          </div>

          <!-- Every plate IN THE DRAWING is a button that takes THAT plate off.
               Tapping the wrong key is the common mistake here, and "Take the
               last one off" only ever removes the outermost. -->
          <div class="count-bar">
            <BarSleeve
              :stack="side"
              :plates="kitPlates"
              :unit="unit"
              :collar-weight="collarWeight"
              :removable="!!side.length"
              :animate="loadingMotion"
              @remove="removeAt"
              @full="full = $event"
            />
          </div>

          <!-- One line, whichever it says, so a full sleeve moves nothing. -->
          <p class="hint serif">
            {{ full ? "Sleeve's full, check this fits your bar." : 'Tap the plates as they went on, inside out.' }}
          </p>

          <PlatePad :plates="kitPlates" :unit="unit" @add="push" />

          <div class="undo">
            <button type="button" class="wu-link" :disabled="!side.length" @click="pop">
              Take the last one off
            </button>
            <button type="button" class="wu-link" :disabled="!side.length" @click="clear">
              Clear the bar
            </button>
          </div>
        </div>
      </div>
    </IonContent>
  </IonPage>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { IonPage, IonContent, IonButtons } from '@ionic/vue'
import { AppHeader, AppLargeTitle } from '../core/ui'
import BarSleeve from '../components/BarSleeve.vue'
import PlatePad from '../components/PlatePad.vue'
import { bare, countBar, fmt, loadBar } from '../lib/loading.js'
import { haptic } from '../core/util.js'
import { currentKit, platesFor } from '../lib/seed.js'
// Both drawings animate plates on and off when the lifter's setting says so:
// Count as each plate is tapped on or off, Load from the last answer's plates
// to the new one's.
import { loadingMotion } from '../lib/prefs.js'

const router = useRouter()
const goKit = () => router.push({ name: 'kit' })

// The same test AppLargeTitle makes: iOS has a large title to sit beside,
// Material does not. Read once at setup, as it does, so nothing mounts twice.
const isIos = typeof document !== 'undefined' && document.documentElement.classList.contains('ios')

const MODES = [{ id: 'load', label: 'Load' }, { id: 'count', label: 'Count' }]

const kit = computed(() => currentKit())
const kitPlates = computed(() => (kit.value ? platesFor(kit.value) : []))
const hasKit = computed(() => !!kit.value && kitPlates.value.length > 0)
const unit = computed(() => kit.value?.unit || 'kg')
const collarWeight = computed(() => Number(kit.value?.collarWeight) || 0)
/** What the bar weighs empty: the bar alone, because collars go on with the first plate. */
const emptyBar = computed(() => (kit.value ? bare(kit.value) / 100 : 0))

const mode = ref('load')
/** BarSleeve says when the sleeve has run out of room; the page says it in words. */
const full = ref(false)

// --- load -------------------------------------------------------------------
const target = ref('')
const result = ref(null)
/** The weight the CURRENT answer belongs to. `asked` moves as soon as the field does. */
const answered = ref(null)
const field = ref(null)

/** The typed weight as a number, or null. A comma is a decimal point in half the world. */
const asked = computed(() => {
  const raw = String(target.value).trim().replace(',', '.')
  if (!raw) return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
})

// ON SUBMIT, NOT ON EVERY KEYSTROKE. Typing "142.75" passes through 1, 14, 142
// and 142.7, and a live calculator shouts "your plates can't make 1kg" at
// somebody who is still typing. The answer arrives when the question is asked.
function run() {
  if (asked.value === null || !kit.value) return
  answered.value = asked.value
  result.value = loadBar(asked.value, kit.value, kitPlates.value)
}

/** A nearest-weight button fills the field AND answers it — half an action is a shrug. */
function use(weight) {
  target.value = fmt(weight)
  run()
}

/** A tap anywhere on the field that is not the Load button means "let me type". */
function focusField(event) {
  if (event.target.closest('button')) return
  field.value?.focus()
}

const problem = computed(() => {
  const r = result.value
  if (!r || r.ok) return null
  const opt = (w, note) => (w == null ? null : { weight: w, label: fmt(w), note })
  if (r.reason === 'under-bar') {
    return {
      line: `${fmt(answered.value ?? 0)} ${unit.value} is under the bar.`,
      hint: `The empty bar is ${fmt(r.above)} ${unit.value}.`,
      options: [opt(r.above, 'empty bar')].filter(Boolean),
    }
  }
  return {
    line: `Your plates can't make ${fmt(answered.value ?? 0)} ${unit.value}.`,
    hint: 'Nearest you can load:',
    options: [opt(r.below, 'under'), opt(r.above, 'over')].filter(Boolean),
  }
})

/** The per-side answer as the design prints it: "25 · 25 · 5 · 2.5 · 1.25". */
const perSide = computed(() => {
  const s = result.value?.ok ? result.value.stack : []
  return s.length ? s.map(fmt).join(' · ') : 'Empty bar'
})

// THE KEYBOARD MUST NOT HIDE THE ANSWER. Without a keyboard plugin the iOS
// keyboard lays over the web view rather than shrinking it, so the layout never
// learns it lost the bottom third of the screen. The visual viewport does: while
// the target field has focus, the height it has lost becomes `--kb`, the screen
// gives that much back, and the stage (and the drawing in it) shrinks to fit
// above the keyboard. A web view that DOES resize reads 0 here and shrinks the
// same stage by itself. Pinch-zoom also shrinks the visual viewport, so a
// zoomed page is left alone.
const kb = ref(0)
const focused = ref(false)
function measureKeyboard() {
  const vv = typeof window !== 'undefined' ? window.visualViewport : null
  if (!vv || !focused.value || Math.abs((vv.scale || 1) - 1) > 0.01) { kb.value = 0; return }
  kb.value = Math.max(0, Math.round(window.innerHeight - vv.height))
}
function setFocused(on) {
  focused.value = on
  measureKeyboard()
}
onMounted(() => window.visualViewport?.addEventListener('resize', measureKeyboard))
onBeforeUnmount(() => window.visualViewport?.removeEventListener('resize', measureKeyboard))

// --- count ------------------------------------------------------------------
// One side, inside out, in the order they were tapped. `countBar` is
// bar + 2 collars + 2 × the side, so the order is the drawing's business only.
const side = ref([])
const push = (w) => { side.value = [...side.value, w] }
const pop = () => { side.value = side.value.slice(0, -1) }
const clear = () => { side.value = [] }
// Take one SPECIFIC plate off, by its position in the stack. The sleeve draws
// `side` in the same order, so the index carries over.
const removeAt = (i) => {
  if (i < 0 || i >= side.value.length) return
  side.value = side.value.filter((_, n) => n !== i)
  haptic('tick')
}
const countTotal = computed(() => (kit.value ? countBar(side.value, kit.value) : 0))

// --- load's drawing -----------------------------------------------------------
/**
 * Nothing is ever passed as `added`: a calculator's answer has no "new" to call
 * out. With the setting on, BarSleeve loads a new answer from the last one's
 * plates, off and on; the still drawing is the same either way.
 */
const stack = computed(() => (result.value?.ok ? result.value.stack : []))
</script>

<style scoped>
/* The bar runs in from the screen's left edge: the page's own padding is how
   far the drawing pulls out. */
.bar-page { --bleed: var(--wu-pad); }

/* THIS SCREEN IS SIZED TO THE PHONE, so its bottom padding has to be exact.
   Fullscreen content already pads itself by the tab bar's own height
   (`--offset-bottom`, home indicator included); app.css adds
   `--tabbar-clearance` on top of that for every page, which here would be
   56px of dead space under the links and a stage 56px shorter than the
   design's. 16px of breathing room, and Ionic's offset does the rest. */
.bar-page ion-content { --padding-bottom: 16px; }

/* THE SCREEN FILLS THE CONTENT AREA, less whatever an open keyboard is lying
   over. The bottom `--offset-bottom + 16px` is already padding and already
   under the keyboard, so only the rest is given back. */
.screen {
  display: flex;
  flex-direction: column;
  min-height: calc(100% - max(0px, var(--kb, 0px) - var(--offset-bottom, 0px) - 16px));
}

/* THE MODE SWITCH, beside the large title. The title is a factory component
   with no slot for it, so the switch is laid over its row from here. */
.head { position: relative; flex: none; }

.head-seg {
  position: absolute;
  top: 50%;
  right: var(--wu-pad);
  transform: translateY(-50%);
  z-index: 10;
}

/* In Material's bar, the switch sits on the page's 24px edge too. */
ion-buttons .modes { margin-inline-end: calc(var(--wu-pad) - 8px); }

.page {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
  padding: 0 var(--wu-pad);
}

/* Bar's labels are a size up from the shared one: the design's 12px, 0.08em. */
.ask-label,
.count-label {
  font-size: 0.75rem;
  letter-spacing: 0.08em;
}

/* --- load ------------------------------------------------------------------ */
.load { padding-top: clamp(16px, 4.7vh, 40px); }

.ask-label { display: block; margin-bottom: 10px; }

.ask { max-width: 34rem; cursor: text; }

/* THE INPUT IS AS WIDE AS ITS TEXT. The hidden copy of the value sizes the
   cell; the input fills it (width 0 contributes nothing to the sizing, the
   min-width then stretches it to the cell). So "kg" sits against "142.5" the
   way the design sets it, instead of across a field-width gap. */
.sizer {
  display: inline-grid;
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
}

.sizer::after {
  content: attr(data-value);
  grid-area: 1 / 1;
  visibility: hidden;
  white-space: pre;
  padding-right: 2px;
  font: 400 48px/1.15 var(--face-serif);
  letter-spacing: -0.01em;
}

.ask .entry {
  grid-area: 1 / 1;
  width: 0;
  min-width: 100%;
  font: 400 48px/1.15 var(--face-serif);
  letter-spacing: -0.01em;
  font-variant-numeric: lining-nums;
}

.field-unit {
  flex: none;
  padding-top: 10px;
  font: 400 0.9375rem/1 var(--face-mono);
  color: var(--dim);
}

.go { flex: none; margin-left: auto; }

/* --- what it could not do --------------------------------------------------- */
.problem {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 34rem;
  margin-top: 16px;
}

.problem-line {
  margin: 0;
  font: 500 0.875rem/1.4 var(--face-mono);
  color: var(--ink);
  font-variant-numeric: tabular-nums;
}

.problem-hint {
  margin: 0;
  font: 400 0.8125rem/1.4 var(--face-mono);
  color: var(--dim);
}

.options { display: flex; flex-wrap: wrap; gap: 8px; }

.option {
  flex: 1 1 8rem;
  justify-content: space-between;
  min-width: 0;
  padding: 0 16px;
}

/* The weight and its unit share a baseline; the button centres the pair. */
.option-in { display: inline-flex; align-items: baseline; gap: 6px; }
.option-weight { font-size: 1.625rem; line-height: 1; }
.option-unit { font-size: 0.8125rem; color: var(--dim); }
.option-note { font-size: 0.8125rem; color: var(--dim); }

/* --- the answer ------------------------------------------------------------- */
/* THE STAGE IS A SIZE CONTAINER, so the drawing can be sized from the height
   the stage was given: the design's 88vw where there is room, smaller over an
   open keyboard or on a short phone — never pushing the plate line off. 112px
   is the answer's lines and the gap; 1.38 is the inverse of the tallest
   plate's height at that span (a 450mm 25 on a 621.6mm photograph), plus the
   18px the drawing keeps under the plates. */
.stage {
  flex: 1 1 0;
  min-height: 190px;
  container-type: size;
  display: flex;
  flex-direction: column;
  justify-content: center;
  --sleeve-span: max(150px, min(88vw, 400px, calc((100cqh - 112px) * 1.38)));
}

.answer {
  flex: none;
  margin-top: clamp(8px, 4cqh, 22px);
  font-variant-numeric: tabular-nums;
}

.per-side {
  margin: 0;
  font: 500 1rem/1.3 var(--face-mono);
  color: var(--ink);
}

.per-side-note {
  margin: 4px 0 0;
  font-size: 1.0625rem;
  font-style: italic;
  line-height: 1.25;
  color: var(--dim);
}

.base-line {
  margin: 0;
  font: 400 0.875rem/1.3 var(--face-mono);
  color: var(--dim);
}

.full-line {
  margin: 6px 0 0;
  font: 400 0.8125rem/1.3 var(--face-mono);
  color: var(--dim);
}

/* --- count ------------------------------------------------------------------ */
/* THE BOX NEVER MOVES. BarSleeve sizes its box from the kit's tallest plate,
   not the stack, and every line below it is one line whatever it says, so
   tapping a plate on or off moves nothing on this screen. The drawing is the
   design's 80vw, held to 34vh on a short phone so the pad stays in reach. */
.count { padding-top: clamp(10px, 3.3vh, 28px); }

.tally { display: flex; flex-direction: column; gap: 6px; }
.tally-line { display: flex; align-items: baseline; gap: 8px; margin: 0; }

.total {
  font-size: min(56px, 15vw);
  line-height: 1;
  color: var(--ink);
  font-variant-numeric: lining-nums;
}

.total-unit {
  font: 400 0.9375rem/1 var(--face-mono);
  color: var(--dim);
}

/* THE DRAWING TAKES THE HEIGHT THAT IS LEFT, up to the design's 80vw. On a
   tall phone that is the design's drawing and the spare room falls below the
   links; on a phone with a notch and a home bar, or an SE, it is smaller and
   the pad and both links still fit. The height depends on the viewport only,
   never on the stack, so the box still never moves as plates go on. Same
   arithmetic as the Load stage: 1.38 is the inverse of the tallest plate's
   height at a given span, 26px the drawing's own label room plus 8px. */
.count-bar {
  flex: 1 1 0;
  min-height: 140px;
  max-height: calc(min(80vw, 364px) * 0.725 + 26px);
  container-type: size;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  --sleeve-span: max(130px, min(80vw, 364px, calc((100cqh - 26px) * 1.38)));
}

.hint {
  margin: 4px 0 clamp(8px, 1.7vh, 14px);
  font-size: 1.0625rem;
  font-style: italic;
  line-height: 1.3;
  color: var(--dim);
}

.undo {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: clamp(6px, 1.7vh, 14px);
}

/* --- no kit ----------------------------------------------------------------- */
.empty {
  align-items: flex-start;
  gap: 16px;
  padding-top: 40px;
}

.empty-line { margin: 0; font-size: 1.625rem; line-height: 1.2; }
</style>
